import React, { useState } from 'react';
import { CalendarWeek, Check, CheckCircleFill, ChevronUp, LockFill } from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';

import { Timeline, TimelineItemProps, Typography } from 'antd';

import Progress from '@/components/shared/Progress';
import { useModuleProgress } from '@/hooks/useProgress';
import { enStatus, IModule } from '@/interfaces/course';
import styled, { css, cx, StyledProp } from '@/styled';

const iconMapper: Record<string, any> = {
  [enStatus.EXPIRED]: <LockFill size={16} />,
  [enStatus.NOT_RELEASED]: <CalendarWeek size={16} />,
  [enStatus.NOT_PURCHASED]: <LockFill size={16} />
};

interface IModuleTimelineProps {
  module: IModule;
  isLast: boolean;
}

const ModuleTimeline = ({ module, className, isLast }: IModuleTimelineProps & StyledProp) => {
  const { id } = useParams();
  const isCurrentModule = Boolean(module.lessons.find(lesson => lesson.id.toString() === id));
  const [expand, setExpand] = useState(isCurrentModule);
  const moduleProgress = useModuleProgress(module.id);
  const navigate = useNavigate();

  const expandModule = () => {
    setExpand(show => !show);
  };

  const items: TimelineItemProps[] = [
    {
      dot: (
        <div className='module-circle-container'>
          <Progress type='circle' showInfo={false} percent={moduleProgress} strokeWidth={5} size={28} />
          {moduleProgress === 100 && <Check size={24} className='module-circle-check' />}
        </div>
      ),
      children: (
        <>
          <Typography.Title
            level={3}
            data-current={isCurrentModule}
            className='module-timeline-title'
            onClick={expandModule}
          >
            {module.title}
          </Typography.Title>
          <Typography.Text data-show={!expand || moduleProgress === 0} data-current={isCurrentModule} className='total'>
            {module.lessons.length + ' aulas'}
          </Typography.Text>
          <div className='module-timeline-expand'>
            <ChevronUp size={16} aria-expanded={!expand} />
          </div>
        </>
      )
    }
  ];

  if (expand) {
    module.lessons.forEach(lesson => {
      items.push({
        dot: lesson.watched ? <CheckCircleFill size={16} /> : iconMapper[lesson.status] ?? <div className='dot' />,
        children: (
          <Typography.Paragraph
            data-current={lesson.id.toString() === id}
            onClick={() => navigate(`/curso/${lesson.hash_course}/aula/${lesson.id}`)}
            ellipsis={{ rows: 2 }}
            className='module-timeline-lesson-title'
          >
            {lesson.title}
          </Typography.Paragraph>
        )
      });
    });
  }

  return (
    <Timeline rootClassName={cx(className, isCurrentModule && 'currentModule', isLast && 'lastModule')} items={items} />
  );
};
export default styled(ModuleTimeline)(
  ({ theme }) => css`
    .total {
      display: none;

      &[data-show='true'] {
        display: block;
      }
    }

    .module-circle-container {
      position: relative;
    }

    .module-circle-check {
      position: absolute;
      left: calc(25% - 5px);
      top: calc(25% - 5px);
    }

    .module-timeline-lesson-title {
      font-size: ${theme.space.pxToRem(14)};
    }

    .module-timeline-title {
      font-size: ${theme.space.pxToRem(16)};
    }

    .module-timeline-expand {
      position: absolute;
      right: 0;
      top: 5px;

      svg {
        transition: transform ${theme.motionDurationFast};
      }

      svg[aria-expanded='true'] {
        transform: rotate(180deg);
      }
    }

    .dot {
      border: 1px solid ${theme.palette.gray[8]};
      border-radius: 100%;
      width: 16px;
      height: 16px;
      margin: 0;
    }

    .ant-typography {
      color: ${theme.colorTextSecondary};
      margin: 0;
      font-weight: 400;
    }

    div.ant-typography {
      cursor: pointer;
    }

    h3.ant-typography {
      line-height: 100%;
      cursor: pointer;
      position: relative;
      top: 4px;
    }

    .ant-typography[data-current='true'] {
      color: ${theme.colorText};
    }

    .ant-timeline-item-head-custom {
      padding-block: 0;
    }

    .ant-timeline-item-head {
      background-color: transparent;
    }

    .ant-timeline-item-tail {
      inset-block-start: 13px;
      height: calc(100% - 16px);
    }

    > li:first-child > div:first-child {
      inset-block-start: 18px !important;
      height: calc(100% - 22px) !important;
    }

    .ant-timeline-item .ant-timeline-item-content {
      margin-inline-start: ${theme.space.pxToRem(32)};
    }

    &.lastModule {
      padding-bottom: 60px;
    }
  `
);
